import { useNavigate } from "react-router-dom";
import { StarIcon } from "@phosphor-icons/react";
import { DegenScore, FlagBadge, verdictColor, MomentumBadge } from "./DegenScore";
import { QuickBuyLink } from "./TradeButton";
import { CreatorRugBadge, RuggedButton } from "./RugControls";
import { AlphaBadge, useAlpha } from "./AlphaBadge";
import { EliteBadge } from "./EliteBadge";
import { TrashButton, reasonMeta } from "./TrashButton";
import { Tier3Badge } from "./Tier3Badge";
import { useProvenDevs } from "../lib/useProvenDevs";
import { newbieTrapBrand } from "../lib/newbieTrap";
import { fmtUsd, fmtPrice, fmtPct, fmtAge, changeColor } from "../lib/format";
import { isWhaleMagnet, whaleAvgBuy } from "../lib/whale";
import { isSurvivor } from "../lib/survivor";
import { CtoBadge } from "./CtoBadge";
import { CandleBadge } from "./CandleBadge";
import { useWatchlist } from "../context/WatchlistContext";
import { useTrash, useCommunityTrash } from "../lib/useTrash";

const Fallback = ({ symbol }) => (
  <div className="w-9 h-9 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-xs text-muted-foreground">
    {(symbol || "?").slice(0, 3)}
  </div>
);

export const CoinRow = ({ coin, rank }) => {
  const navigate = useNavigate();
  const { isWatched, toggle } = useWatchlist();
  const { isTrashed, reasonFor } = useTrash();
  const { isCommunityScam, scamCount, scamReason } = useCommunityTrash();
  const watched = isWatched(coin.address);
  const trashed = isTrashed(coin.address);
  const trashReason = trashed ? reasonMeta(reasonFor(coin.address)) : null;
  const scam = isCommunityScam(coin.address);
  const { provenFor } = useProvenDevs();
  const proven = provenFor(coin.creator);
  const trap = newbieTrapBrand(coin.name, coin.symbol);
  const alpha = useAlpha()(coin.creator);
  const ch24 = coin.priceChange?.h24 ?? 0;

  return (
    <tr
      data-testid={`coin-row-${coin.symbol}`}
      onClick={() => navigate(`/coin/${coin.address}`)}
      className={`border-b cursor-pointer transition-colors duration-150 group ${
        trashed
          ? "border-white/5 opacity-40 hover:opacity-70 hover:bg-white/5"
          : coin.lowQuality
          ? "border-danger/10 opacity-60 hover:opacity-90 hover:bg-white/5"
          : alpha
          ? "border-[#C9A227]/30 bg-[#C9A227]/[0.06] hover:bg-[#C9A227]/[0.12] border-l-2 border-l-[#F0C000]"
          : "border-white/5 hover:bg-white/5"
      }`}
    >
      <td className="px-3 py-3 text-right font-mono text-xs text-muted-foreground w-10">{rank}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          {coin.image ? (
            <img
              src={coin.image}
              alt={coin.symbol}
              className="w-9 h-9 object-cover border border-white/10"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <Fallback symbol={coin.symbol} />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="font-heading font-bold text-sm truncate max-w-[140px] group-hover:text-neon transition-colors">
                {coin.symbol}
              </div>
              <AlphaBadge creator={coin.creator} compact />
              <EliteBadge coin={coin} compact />
              <Tier3Badge address={coin.address} symbol={coin.symbol} compact />
              {coin.vamp?.status === "vamp" && (
                <span data-testid={`vamp-badge-${coin.symbol}`} title={`Possible VAMP — a copycat of an older $${coin.symbol}${coin.vamp.original ? ` (OG ~${fmtUsd(coin.vamp.original.mcap)} mcap)` : ""}. Verify before aping.`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-danger/15 border border-danger/40 text-danger tracking-wide">🧛 VAMP</span>
              )}
              {coin.vamp?.status === "og" && (
                <span title={`Original $${coin.symbol} ticker (oldest ${coin.vamp.dupes} copycat${coin.vamp.dupes === 1 ? "" : "s"} exist)`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-neon/10 border border-neon/30 text-neon tracking-wide">OG</span>
              )}
              {coin.velocity?.direction === "up" && (
                <span data-testid={`velocity-badge-${coin.symbol}`} title={`Up ${coin.velocity.multiplier}× in the last hour (+${Math.round(coin.velocity.changeH1)}%) — fast mover`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-neon/15 border border-neon/40 text-neon tracking-wide">🚀 {coin.velocity.multiplier}× 1h</span>
              )}
              {coin.velocity?.direction === "down" && (
                <span data-testid={`velocity-badge-${coin.symbol}`} title={`Down ${Math.round(coin.velocity.changeH1)}% in the last hour — dipping fast`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-danger/15 border border-danger/40 text-danger tracking-wide">🔻 {Math.round(coin.velocity.changeH1)}% 1h</span>
              )}
              {isWhaleMagnet(coin) && (
                <span data-testid={`whale-badge-${coin.symbol}`} title={`Whale Magnet — avg trade ~$${whaleAvgBuy(coin).toLocaleString()} in the last hour with buy pressure. Whale-sized money is flowing in.`}
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide" style={{ background: "rgba(34,211,238,0.15)", border: "1px solid rgba(34,211,238,0.5)", color: "#22D3EE" }}>🐋 WHALE</span>
              )}
              {isSurvivor(coin) && (
                <span data-testid={`survivor-badge-${coin.symbol}`} title="Survivor — this launch lasted: ≥10 min old, real liquidity, still trading, not dumped 90%. Not a 5-minute rug."
                  className="text-[9px] font-mono px-1 py-0.5 bg-neon/10 border border-neon/40 text-neon tracking-wide">🛡️ SURVIVOR</span>
              )}
              <CtoBadge cto={coin.cto} testid={`cto-badge-${coin.symbol}`} />
              <CandleBadge candle={coin.candle} />
              {coin.reawakening && (
                <span data-testid={`reawaken-badge-${coin.symbol}`}
                  title={`Waking up — ${coin.reawakening.volPct}% of 24h volume in the last hour · ${coin.reawakening.buys} buys vs ${coin.reawakening.sells} sells (1h)`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-amber-400/15 border border-amber-400/50 text-amber-300 tracking-wide">⚡ WAKING UP</span>
              )}
              {coin.botSuspect && (
                <span data-testid={`bot-badge-${coin.symbol}`}
                  title={`Possible wash volume — ${coin.botSuspect.txns} trades (1h), ~$${coin.botSuspect.avgUsd} avg, volume ~${coin.botSuspect.volLiqX}× liquidity. Heuristic — check Holder Flow on the coin page.`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-red-500/15 border border-red-500/50 text-red-300 tracking-wide">🤖 BOT-HEAVY</span>
              )}
              {coin.conviction != null && coin.conviction >= 45 && (
                <span data-testid={`conviction-badge-${coin.symbol}`} title={`Conviction ${coin.conviction}/100 — credibility-blind (Volume + Survival + Buy Pressure only). ${coin.conviction >= 75 ? "ANSEM-tier: a real runner even if its token info looks weak — size up if it checks out." : "Solid underlying demand."}`}
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide"
                  style={coin.conviction >= 75
                    ? { background: "rgba(240,192,0,0.18)", border: "1px solid rgba(240,192,0,0.6)", color: "#F0C000" }
                    : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#9aa4b2" }}>
                  {coin.conviction >= 75 ? "👀 " : "⚡"}{coin.conviction}
                </span>
              )}
              {coin.organicRunner && (
                <span data-testid={`runner-badge-${coin.symbol}`} title="Organic Runner — shook out hard from its peak and is now bouncing back with buyers. The survivor-then-run pattern."
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide" style={{ background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.5)", color: "#4ADE80" }}>🌱 RUNNER</span>
              )}
              {coin.lpUnlocked && (
                <span data-testid={`lp-badge-${coin.symbol}`} title="LP UNLOCKED — liquidity isn't locked/burned, the dev can pull it at any time. Real demand can still rug fast. Take profits quick."
                  className="text-[9px] font-mono px-1 py-0.5 bg-danger/20 border border-danger/60 text-danger tracking-wide font-bold">🔓 LP UNLOCKED</span>
              )}
              {coin.lpLocked && (
                <span data-testid={`lp-safe-badge-${coin.symbol}`} title="LP LOCKED/BURNED — liquidity is locked or burned, so the dev can't pull it. One less rug vector (still DYOR on holders & authorities)."
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide font-bold" style={{ background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.6)", color: "#4ADE80" }}>🔒 LP LOCKED</span>
              )}
              {coin.nameCollision && (coin.nameLeader ? (
                <span data-testid={`name-real-${coin.symbol}`} title={`${coin.nameCollisionCount} live tokens share this ticker — this one ranks #1 on liquidity, volume, age & score (most likely the real one). Still DYOR.`}
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide" style={{ background: "rgba(0,229,153,0.12)", border: "1px solid rgba(0,229,153,0.5)", color: "#00E599" }}>✅ LIKELY REAL (1/{coin.nameCollisionCount})</span>
              ) : (
                <span data-testid={`name-copy-${coin.symbol}`} title={`${coin.nameCollisionCount} live tokens share this ticker — this is #${coin.nameRank} by liquidity/volume/age. Probably a copycat.`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-danger/10 border border-danger/40 text-danger tracking-wide">⚠️ COPY #{coin.nameRank}/{coin.nameCollisionCount}</span>
              ))}
              {trashed && (
                <span data-testid={`trash-badge-${coin.symbol}`} title={`You marked this as trash${trashReason ? ` — ${trashReason.label}` : ""}`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-danger/20 border border-danger/50 text-danger tracking-wide">{trashReason ? `${trashReason.emoji} ${trashReason.label.toUpperCase()}` : "🗑️ TRASH"}</span>
              )}
              {coin.lowQuality && (
                <span data-testid={`lowquality-badge-${coin.symbol}`} title={coin.lowQualityReason || "Low quality — fails a basic liquidity/quality check. A real runner never looks like this."}
                  className="text-[9px] font-mono px-1 py-0.5 bg-danger/20 border border-danger/50 text-danger tracking-wide font-bold">⚠️ LOW QUALITY</span>
              )}
              {scam && (
                <span data-testid={`scam-badge-${coin.symbol}`} title={`Flagged as a scam by ${scamCount(coin.address)} AlphaApeing users${scamReason(coin.address) ? ` — mostly ${reasonMeta(scamReason(coin.address))?.label || ""}` : ""}`}
                  className="text-[9px] font-mono px-1 py-0.5 bg-danger/25 border border-danger/60 text-danger tracking-wide font-bold">☠️ SCAM {scamCount(coin.address)}{scamReason(coin.address) && reasonMeta(scamReason(coin.address)) ? ` · ${reasonMeta(scamReason(coin.address)).emoji}` : ""}</span>
              )}
              {coin.discovery === "organic" && (
                <span data-testid={`organic-badge-${coin.symbol}`} title="Organic — surfaced via trending / high-volume pools. No paid promotion; real demand."
                  className="text-[9px] font-mono px-1 py-0.5 bg-neon/10 border border-neon/40 text-neon tracking-wide">🌱 ORGANIC</span>
              )}
              {coin.discovery === "boosted" && (
                <span data-testid={`boosted-badge-${coin.symbol}`} title="Paid Boosted — appeared via a paid DexScreener promotion. Someone paid to surface it; verify the demand is real."
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide" style={{ background: "rgba(240,192,0,0.12)", border: "1px solid rgba(240,192,0,0.45)", color: "#F0C000" }}>💸 BOOSTED</span>
              )}
              {proven && (
                <span data-testid={`proven-dev-${coin.symbol}`} title={`${proven.name} is one of your tracked devs — ${proven.hitRate}% of their ${proven.coins} tracked coins ran ≥2× from when we first spotted them. A proven builder.`}
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide font-bold" style={{ background: "rgba(240,192,0,0.15)", border: "1px solid rgba(240,192,0,0.6)", color: "#F0C000" }}>⭐ {proven.hitRate}% HIT</span>
              )}
              {trap && (
                <span data-testid={`newbie-trap-${coin.symbol}`} title={`Potential Newbie Trap — this token's name copies the real brand "${trap}". It may exist to fool newcomers into thinking it's the official ${trap}. Could still be legit — check the chart, socials & official links before aping.`}
                  className="text-[9px] font-mono px-1 py-0.5 tracking-wide font-bold" style={{ background: "rgba(240,160,32,0.15)", border: "1px solid rgba(240,160,32,0.6)", color: "#F0A020" }}>⚠️ NEWBIE TRAP? ({trap})</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs text-muted-foreground truncate max-w-[130px]">{coin.name}</div>
              {coin.traction && (
                <span title={`Community traction ${coin.traction.score}/100 — buys ${coin.traction.buyRatio24}%, ${coin.traction.traders24} traders/24h`}
                  className="text-[9px] font-mono px-1 py-0.5 border tracking-wide shrink-0"
                  style={{ color: coin.traction.score >= 55 ? "#00E599" : coin.traction.score >= 35 ? "#F0C000" : "#888", borderColor: "rgba(255,255,255,0.12)" }}>
                  📈 {coin.traction.label}
                </span>
              )}
            </div>
            <CreatorRugBadge creator={coin.creator} className="mt-1" />
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-right font-mono text-sm">{fmtPrice(coin.priceUsd)}</td>
      <td className="px-3 py-3 text-right font-mono text-sm" style={{ color: changeColor(ch24) }}>
        {fmtPct(ch24)}
      </td>
      <td className="px-3 py-3 text-right font-mono text-sm hidden md:table-cell">
        {fmtUsd(coin.volume?.h24)}
      </td>
      <td className="px-3 py-3 text-right font-mono text-sm hidden lg:table-cell">
        {fmtUsd(coin.marketCap)}
      </td>
      <td className="px-3 py-3 text-right font-mono text-sm hidden lg:table-cell">
        {fmtUsd(coin.liquidity)}
      </td>
      <td className="px-3 py-3 text-right font-mono text-xs text-muted-foreground hidden sm:table-cell">
        {fmtAge(coin.ageHours)}
      </td>
      <td className="px-3 py-3 text-center hidden md:table-cell">
        <MomentumBadge momentum={coin.momentum} showSpike />
      </td>
      <td className="px-3 py-3 text-center hidden lg:table-cell">
        {coin.velocity && coin.velocity.direction !== "flat" ? (
          <span className="font-mono text-xs font-semibold" style={{ color: coin.velocity.direction === "up" ? "#00E599" : "#FF3366" }}>
            {coin.velocity.direction === "up" ? `${coin.velocity.multiplier}×` : `${Math.round(coin.velocity.changeH1)}%`}
          </span>
        ) : (
          <span className="font-mono text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-3 text-center hidden xl:table-cell">
        {coin.conviction != null ? (
          <span
            className="font-mono text-sm font-bold"
            title={`Conviction ${coin.conviction}/100 — credibility-blind (Volume + Survival + Buy Pressure only)`}
            style={{ color: coin.conviction >= 75 ? "#F0C000" : coin.conviction >= 45 ? "#00E599" : "#666" }}
          >
            {coin.conviction}
          </span>
        ) : (
          <span className="font-mono text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-col items-center gap-1">
          <DegenScore score={coin.score.total} size={40} />
          <span className="font-mono text-[9px] tracking-wider" style={{ color: verdictColor(coin.score.verdict) }}>
            {coin.score.verdict}
          </span>
        </div>
      </td>
      <td className="px-3 py-3 hidden xl:table-cell">
        <div className="flex flex-wrap gap-1 max-w-[220px]">
          {coin.score.flags.slice(0, 3).map((f, i) => <FlagBadge key={i} flag={f} />)}
        </div>
      </td>
      <td className="px-3 py-3 text-center">
        <div className="flex items-center justify-center gap-1.5">
          <QuickBuyLink coin={coin} testid={`buy-btn-${coin.symbol}`} />
          <RuggedButton mint={coin.address} symbol={coin.symbol} creator={coin.creator} size={17} />
          <TrashButton mint={coin.address} symbol={coin.symbol} testid={`trash-btn-${coin.symbol}`} icon="trash" size={17} className="hover:bg-white/10" />
          <button
            data-testid={`watch-btn-${coin.symbol}`}
            onClick={(e) => { e.stopPropagation(); toggle(coin.address); }}
            className="p-1.5 hover:bg-white/10 transition-colors"
            aria-label="watch"
          >
            <StarIcon size={18} weight={watched ? "fill" : "regular"} color={watched ? "#F5A623" : "#666"} />
          </button>
        </div>
      </td>
    </tr>
  );
};
